import {Injector} from "@angular/core"
import {AsyncCacheMap} from "@app/common/helpers/async-cache-map/async-cache-map"
import {LatestMaterialRevisionForMaterialGraphBatchApiCall} from "@app/common/services/material-graph/latest-material-revision-for-material-graph-batch-api-call"
import {MaterialRevisionForMaterialGraphBatchApiCall} from "@app/common/services/material-graph/material-revision-for-material-graph-batch-api-call"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"
import {deserializeMaterialGraph, MaterialGraph} from "@cm/material-nodes"
import {catchError, from, map, Observable, switchMap} from "rxjs"

export class MaterialGraphCache extends AsyncCacheMap<string, MaterialGraph, [number, boolean, boolean]> {
    constructor(injector: Injector) {
        const dataResolverService = injector.get(DataResolverService)
        const materialRevisionBatchCall = injector.get(MaterialRevisionForMaterialGraphBatchApiCall)
        const latestMaterialRevisionBatchCall = injector.get(LatestMaterialRevisionForMaterialGraphBatchApiCall)
        super((_, [id, isRevision, flatten]) => {
            return (
                isRevision
                    ? from(materialRevisionBatchCall.fetch({legacyId: id}))
                    : from(latestMaterialRevisionBatchCall.fetch({legacyId: id})).pipe(map((material) => material.latestCyclesRevision))
            ).pipe(
                map((revision) => {
                    if (!revision) throw new Error(`Material has no latest valid revision`)
                    const materialGraph = deserializeMaterialGraph(revision.graphNode)
                    materialGraph.updateParameters({
                        materialLegacyId: revision.material.legacyId,
                        materialRevisionLegacyId: revision.legacyId,
                    })
                    return materialGraph
                }),
                switchMap((materialGraph) => {
                    if (flatten) {
                        return from(materialGraph.flatten(dataResolverService.resolve))
                    } else {
                        return from(Promise.resolve(materialGraph))
                    }
                }),
                catchError((error) => {
                    const identifier = isRevision ? `material revision id: ${id}` : `material id: ${id}`
                    if (error instanceof Error) {
                        error.message = `Failed to load ${identifier}: ${error.message}`
                        throw error
                    } else throw new Error(`Failed to load ${identifier}: ${error}`)
                }),
            )
        })
    }

    evaluateMaterial(materialId: number, flatten: boolean): Observable<MaterialGraph> {
        const isRevision = false
        return this.get(`${materialId}-${isRevision}-${flatten}`, [materialId, isRevision, flatten])
    }

    cachedLatestRevision(materialId: number): number | undefined {
        return [true, false]
            .map((flatten) => this.getSync(`${materialId}-false-${flatten}`)?.parameters.materialRevisionLegacyId)
            .find((id) => id !== undefined)
    }

    forgetLatestRevision(materialId: number): void {
        for (const flatten of [true, false]) this.delete(`${materialId}-false-${flatten}`)
    }

    evaluateMaterialRevision(materialRevisionId: number, flatten: boolean): Observable<MaterialGraph> {
        const isRevision = true
        return this.get(`${materialRevisionId}-${isRevision}-${flatten}`, [materialRevisionId, isRevision, flatten])
    }
}
